import PageHeader from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import ValidationErrors from '@/components/validation-errors';
import { centsToInput, formatMoney, parseMoney } from '@/lib/format';
import OptionSelect from '@/pages/apps/components/option-select';
import {
  app_customers_path,
  app_invoices_path,
  edit_app_list_record_path,
  new_app_invoice_path,
} from '@/rails/routes';
import type { AppShell, Customer, InvoiceLine } from '@/types/platform';
import { Link, router, useForm } from '@inertiajs/react';
import { useRef, useState, type FormEvent } from 'react';

interface InvoiceFormProps {
  customerId: string | null;
  memo: string | null;
  dueOn: string | null;
  recordId: string | null;
  lines: InvoiceLine[];
}

interface SourceRecord {
  id: string;
  title: string;
  listKey: string;
}

interface InvoicesNewProps {
  app: AppShell;
  customers: Customer[];
  form: InvoiceFormProps;
  record: SourceRecord | null;
  errors: string[];
}

// One line while it is being typed. The price is dollars as text until it is saved as cents.
interface LineDraft {
  key: string;
  description: string;
  quantity: string;
  unitAmount: string;
}

interface InvoiceFormState {
  customerId: string;
  memo: string;
  dueOn: string;
  lines: LineDraft[];
}

function blankLine(key: string): LineDraft {
  return { key, description: '', quantity: '1', unitAmount: '' };
}

function initialState(form: InvoiceFormProps): InvoiceFormState {
  return {
    customerId: form.customerId ?? '',
    memo: form.memo ?? '',
    dueOn: (form.dueOn ?? '').slice(0, 10),
    lines:
      form.lines.length > 0
        ? form.lines.map((line, index) => ({
            key: `line-${index + 1}`,
            description: line.description ?? '',
            quantity: String(line.quantity ?? 1),
            unitAmount: centsToInput(line.unitAmountCents),
          }))
        : [blankLine('line-1')],
  };
}

function wholeQuantity(value: string): number {
  const quantity = Number(value);

  return Number.isInteger(quantity) && quantity > 0 ? quantity : 0;
}

function lineTotal(line: LineDraft): number {
  return wholeQuantity(line.quantity) * (parseMoney(line.unitAmount) ?? 0);
}

const InvoicesNew = ({ app, customers, form: saved, record, errors }: InvoicesNewProps) => {
  const [initial] = useState(() => initialState(saved));
  const nextLine = useRef(initial.lines.length + 1);
  const form = useForm<InvoiceFormState>(initial);
  const [unreadablePrices, setUnreadablePrices] = useState<string[]>([]);
  const totalCents = form.data.lines.reduce((sum, line) => sum + lineTotal(line), 0);
  const customersPath = app_customers_path(app.id);

  const addLine = () => {
    const key = `line-${nextLine.current}`;
    nextLine.current += 1;
    form.setData((data) => ({ ...data, lines: [...data.lines, blankLine(key)] }));
  };

  const updateLine = (key: string, patch: Partial<Omit<LineDraft, 'key'>>) => {
    form.setData((data) => ({
      ...data,
      lines: data.lines.map((line) => (line.key === key ? { ...line, ...patch } : line)),
    }));
  };

  const removeLine = (key: string) => {
    form.setData((data) => ({ ...data, lines: data.lines.filter((line) => line.key !== key) }));
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const unreadable = form.data.lines
      .filter((line) => parseMoney(line.unitAmount) === null)
      .map((line) => line.key);
    setUnreadablePrices(unreadable);

    if (unreadable.length === 0) {
      form.transform((data) => ({
        invoice: {
          customer_id: data.customerId === '' ? null : data.customerId,
          memo: data.memo.trim() === '' ? null : data.memo,
          due_on: data.dueOn === '' ? null : data.dueOn,
          record_id: saved.recordId,
          lines: data.lines.map((line) => ({
            description: line.description,
            quantity: Number(line.quantity),
            unit_amount_cents: parseMoney(line.unitAmount),
          })),
        },
      }));
      form.post(app_invoices_path(app.id), {
        // A refused invoice is rendered at the address it was sent to, which is
        // not a page of its own. Put this form's address back, with the record
        // it came from, so a refresh lands here.
        onError: () =>
          router.replace({
            url: new_app_invoice_path(app.id, saved.recordId ? { record_id: saved.recordId } : {}),
            preserveState: true,
            preserveScroll: true,
          }),
      });
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="New invoice" description="It is saved as a draft. Nothing is sent yet." />

      {record ? (
        <p className="text-sm">
          From{' '}
          <Link
            href={edit_app_list_record_path(app.id, record.listKey, record.id)}
            className="font-medium underline"
          >
            {record.title}
          </Link>
        </p>
      ) : null}

      <form onSubmit={submit} className="space-y-4">
        <ValidationErrors errors={errors} />

        <Card>
          <CardContent className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="invoice-customer">Customer</Label>
              <OptionSelect
                id="invoice-customer"
                value={form.data.customerId}
                onChange={(customerId) => form.setData('customerId', customerId)}
                options={customers.map((customer) => ({
                  value: customer.id,
                  label: `${customer.name} (${customer.email})`,
                }))}
                placeholder={customers.length === 0 ? 'No customers yet' : 'Choose a customer'}
                required
              />
              <p className="text-sm">
                <Link href={customersPath} className="font-medium underline">
                  Add a customer
                </Link>
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="invoice-due-on">Due date</Label>
              <Input
                id="invoice-due-on"
                type="date"
                value={form.data.dueOn}
                onChange={(event) => form.setData('dueOn', event.target.value)}
              />
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="invoice-memo">Memo</Label>
              <Textarea
                id="invoice-memo"
                rows={2}
                value={form.data.memo}
                onChange={(event) => form.setData('memo', event.target.value)}
              />
              <p className="text-muted-foreground text-xs">Shown to the customer on the invoice.</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              <h2>Lines</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <ul className="space-y-4">
              {form.data.lines.map((line, index) => {
                const unreadable = unreadablePrices.includes(line.key);
                const priceErrorId = `${line.key}-price-error`;

                return (
                  <li key={line.key}>
                    <fieldset className="grid grid-cols-2 gap-3 sm:grid-cols-[1fr_6rem_9rem_auto] sm:items-end">
                      <legend className="sr-only">Line {index + 1}</legend>
                      <div className="col-span-2 space-y-1 sm:col-span-1">
                        <Label htmlFor={`${line.key}-description`} className="text-xs">
                          Description
                        </Label>
                        <Input
                          id={`${line.key}-description`}
                          value={line.description}
                          onChange={(event) =>
                            updateLine(line.key, { description: event.target.value })
                          }
                          required
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor={`${line.key}-quantity`} className="text-xs">
                          Quantity
                        </Label>
                        <Input
                          id={`${line.key}-quantity`}
                          type="number"
                          inputMode="numeric"
                          min={1}
                          step={1}
                          value={line.quantity}
                          onChange={(event) =>
                            updateLine(line.key, { quantity: event.target.value })
                          }
                          required
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor={`${line.key}-price`} className="text-xs">
                          Unit price ($)
                        </Label>
                        <Input
                          id={`${line.key}-price`}
                          type="text"
                          inputMode="decimal"
                          placeholder="0.00"
                          value={line.unitAmount}
                          onChange={(event) =>
                            updateLine(line.key, { unitAmount: event.target.value })
                          }
                          required
                          aria-invalid={unreadable || undefined}
                          aria-describedby={unreadable ? priceErrorId : undefined}
                        />
                      </div>
                      <div className="col-span-2 flex items-center justify-between gap-3 sm:col-span-1">
                        <span className="text-muted-foreground text-sm tabular-nums sm:hidden">
                          {formatMoney(lineTotal(line))}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeLine(line.key)}
                          disabled={form.data.lines.length === 1}
                          aria-label={`Remove line ${index + 1}`}
                        >
                          Remove
                        </Button>
                      </div>
                      {unreadable ? (
                        <p id={priceErrorId} className="text-destructive col-span-full text-sm">
                          Enter the unit price as a dollar amount, like 1840.50.
                        </p>
                      ) : null}
                    </fieldset>
                  </li>
                );
              })}
            </ul>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
              <Button type="button" variant="outline" onClick={addLine}>
                Add line
              </Button>
              <p className="text-sm">
                <span className="text-muted-foreground">Total </span>
                <span className="text-base font-semibold tabular-nums">
                  {formatMoney(totalCents)}
                </span>
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" disabled={form.processing}>
            Save draft
          </Button>
          <Button asChild variant="ghost">
            <Link href={app_invoices_path(app.id)}>Cancel</Link>
          </Button>
        </div>
      </form>
    </div>
  );
};

export default InvoicesNew;
